import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  ACTION_PLAN_CATEGORIES,
  ActionPlanActivity,
  ACTION_PLAN_SYSTEMS,
  ACTION_PLAN_SYSTEMS_ICONS,
  ACTION_PLAN_CATEGORIES_ICONS,
} from '../ActionPlan/action-plan';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { Chip } from '@lifeomic/chroma-react/components/Chip';
import { AutoSuggest, AutoSuggestListItem } from '../../../AutoSuggest';
import { startCase } from 'lodash';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { isAiGenerated } from '../ExecutiveSummary/utils';
import { Alert, AlertBody } from '@lifeomic/chroma-react/components/Alert';

import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';

import { useExecutiveSummaryData } from '../ExecutiveSummary/Context';
import { theme } from '../../../../theme/phc';

const messages = defineMessages({
  save: {
    id: 'actionPlan.actionItemModal.save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'actionPlan.actionItemModal.cancel',
    defaultMessage: 'Cancel',
  },
  addActionItem: {
    id: 'actionPlan.actionItemModal.addActionItem',
    defaultMessage: 'Add action item',
  },
  editActionItem: {
    id: 'actionPlan.actionItemModal.editActionModal',
    defaultMessage: 'Edit action item',
  },
  search: {
    id: 'actionPlan.actionItemModal.search',
    defaultMessage: 'Search or type to add…',
  },
  keyFindings: {
    id: 'actionPlan.actionItemModal.keyFindings',
    defaultMessage: 'Key Findings',
  },
  category: {
    id: 'actionPlan.actionItemModal.category',
    defaultMessage: 'Category',
  },
  system: {
    id: 'actionPlan.actionItemModal.system',
    defaultMessage: 'System',
  },
  aiSuggested: {
    id: 'actionPlan.actionItemModal.aiSuggested',
    defaultMessage: 'AI Suggested',
  },
  aiSuggestedAlert: {
    id: 'actionPlan.actionItemModal.aiSuggestedAlert',
    defaultMessage:
      'This action item was suggested by AI. Saving any changes will remove this label.',
  },
});

type ActionItemModalProps = {
  activity: ActionPlanActivity | undefined;
  isOpen: boolean;
  title: string;
  onConfirm: (activity: fhir.CarePlanActivity, index?: number) => void;
  onDismiss: () => void;
};

const useStyles = makeStyles(theme => ({
  modalRoot: {
    minWidth: theme.pxToRem(800),
    overflow: 'none',
  },
  modalContent: {
    overflow: 'hidden',
  },
  descriptionTextArea: {
    minHeight: theme.pxToRem(300),
  },
  mainFormBox: {
    flex: 9,
  },
  sideFormBox: {
    flex: 4,
  },
  radio: {
    '&&': {
      marginBottom: theme.spacing(1),
    },
  },
  modalActions: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  alertBody: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2.5),
  },
  aiSuggestedPill: {
    color: theme.palette.common.white,
    height: theme.pxToRem(25),
    padding: theme.spacing(1),
  },
}));

export const ActionItemModal = ({
  activity: activityIn,
  isOpen,
  title,
  onConfirm,
  onDismiss,
}: ActionItemModalProps) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activity, setActivity] = useState(
    activityIn ?? new ActionPlanActivity(),
  );
  const { executiveSummary } = useExecutiveSummaryData();

  useEffect(
    () => setActivity(activityIn?.copy() ?? new ActionPlanActivity()),
    [activityIn],
  );

  const suggestions = useMemo(() => {
    if (!executiveSummary) {
      return [];
    }

    const filtered = executiveSummary
      .getKeyFindings()
      .filter(term => term.toLowerCase().includes(searchTerm.toLowerCase()));

    if (filtered.length === 0 && searchTerm) {
      return [searchTerm];
    }

    return filtered;
  }, [executiveSummary, searchTerm]);

  if (!executiveSummary) {
    return null;
  }

  return (
    <Modal
      className={classes.modalRoot}
      contentClassName={classes.modalContent}
      isOpen={isOpen}
      title={title}
      isFormContent
      onDismiss={onDismiss}
      size={1}
      actions={
        <div className={classes.modalActions}>
          <Button onClick={onDismiss} variant="text">
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            disabled={!activity.getTitle()}
            onClick={() => onConfirm(activity.activity)}
          >
            {formatMessage(messages.save)}
          </Button>
        </div>
      }
    >
      <Box direction="column" gap={2.5} fullHeight fullWidth>
        {isAiGenerated(activity.activity.detail) ? (
          <Alert>
            <AlertBody className={classes.alertBody}>
              <Pill
                variant="highlight"
                className={classes.aiSuggestedPill}
                label={formatMessage(messages.aiSuggested)}
              />
              <Text size="subbody">
                {formatMessage(messages.aiSuggestedAlert)}
              </Text>
            </AlertBody>
          </Alert>
        ) : null}
        <Box direction="row" gap={2.5} fullHeight fullWidth>
          <FormBox className={classes.mainFormBox} fullHeight>
            <TextField
              placeholder="Title (required)"
              aria-label="Title"
              value={activity.getTitle()}
              onChange={e => {
                activity.setTitle(e.target.value);
                setActivity(activity.copy());
              }}
              fullWidth
              required
            />
            <TextArea
              textAreaClassName={classes.descriptionTextArea}
              placeholder="Description"
              aria-label="Description"
              fullWidth
              value={activity.getDescription()}
              onChange={e => {
                activity.setDescription(e.target.value);
                setActivity(activity.copy());
              }}
              rows={10}
            />
          </FormBox>
          <Box className={classes.sideFormBox} direction="column" gap={2}>
            <Box direction="column" gap={1}>
              <Select
                label={formatMessage(messages.system)}
                onChange={system => {
                  activity.setSystem(system);
                  setActivity(activity.copy());
                }}
                value={activity.getSystem()}
                fullWidth
                selectedOptionDisplay={option => {
                  const Icon =
                    ACTION_PLAN_SYSTEMS_ICONS[
                      option.value as keyof typeof ACTION_PLAN_SYSTEMS_ICONS
                    ];
                  return (
                    <Text size="subbody">
                      {Icon && (
                        <Icon
                          color={theme.palette.primary.main}
                          style={{ paddingRight: theme.spacing(1) }}
                        />
                      )}
                      {startCase(option.value)}
                    </Text>
                  );
                }}
              >
                {ACTION_PLAN_SYSTEMS.map(system => {
                  const Icon = ACTION_PLAN_SYSTEMS_ICONS[system];
                  return (
                    <SelectOption key={system} value={system}>
                      <Text size="subbody">
                        {Icon && (
                          <Icon
                            color={theme.palette.primary.main}
                            style={{ paddingRight: theme.spacing(1) }}
                          />
                        )}
                        {startCase(system)}
                      </Text>
                    </SelectOption>
                  );
                })}
              </Select>
              <Select
                label={formatMessage(messages.category)}
                onChange={category => {
                  activity.setCategory(category);
                  setActivity(activity.copy());
                }}
                value={activity.getCategory()}
                fullWidth
                selectedOptionDisplay={option => {
                  const Icon =
                    ACTION_PLAN_CATEGORIES_ICONS[
                      option.value as keyof typeof ACTION_PLAN_CATEGORIES_ICONS
                    ];
                  return (
                    <Text size="subbody">
                      {Icon && (
                        <Icon
                          color={theme.palette.primary.main}
                          style={{ paddingRight: theme.spacing(1) }}
                        />
                      )}
                      {startCase(option.value)}
                    </Text>
                  );
                }}
              >
                {ACTION_PLAN_CATEGORIES.map(category => {
                  const Icon = ACTION_PLAN_CATEGORIES_ICONS[category];
                  return (
                    <SelectOption key={category} value={category}>
                      <Text size="subbody">
                        {Icon && (
                          <Icon
                            color={theme.palette.primary.main}
                            style={{ paddingRight: theme.spacing(1) }}
                          />
                        )}
                        {startCase(category)}
                      </Text>
                    </SelectOption>
                  );
                })}
              </Select>
              <AutoSuggest
                inputLabel={formatMessage(messages.keyFindings)}
                inputProps={{
                  fullWidth: true,
                }}
                inputPlaceholder={formatMessage(messages.search)}
                fetchSuggestions={term => setSearchTerm(term)}
                getSuggestionValue={finding => finding}
                onSuggestionSelected={({
                  suggestion,
                }: {
                  suggestion: string;
                }) => {
                  activity.addKeyFinding(suggestion);
                  setActivity(activity.copy());
                }}
                isLoading={false}
                suggestions={suggestions}
                renderSuggestion={(term: any, isHighlighted: boolean) => (
                  <AutoSuggestListItem
                    title={term}
                    isHighlighted={isHighlighted}
                  />
                )}
              />
              <Box flexWrap direction="row" gap={1}>
                {activity.getKeyFindings().map(finding => (
                  <Chip
                    key={finding}
                    onDelete={() => {
                      activity.removeKeyFinding(finding);
                      setActivity(activity.copy());
                    }}
                    style={{
                      margin: 0,
                    }}
                    label={finding}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
